/**
 * NOS-181: per-page SEO resolution shared by the public renderers
 * (PublicWebsitePage — SPA path — and PublicNonprofitSitePage; consumed by
 * Astro in M22). The `pages.seo_title` / `seo_description` columns carry the
 * source site's <title> / meta description on imported pages and are editable
 * in page settings. Both are nullable — pre-NOS-181 pages fall back to the
 * legacy `meta.title` / `meta.description` JSONB overrides, then to the page
 * title, so existing imports and non-imported pages render unchanged.
 */

export interface PublicPageSeo {
  title: string | null
  seo_title?: string | null
  seo_description?: string | null
  meta: Record<string, unknown>
}

function metaString(meta: Record<string, unknown>, key: string): string {
  const v = meta[key]
  return typeof v === "string" ? v.trim() : ""
}

/**
 * The <title> for a public page: `seo_title` column, else the legacy
 * `meta.title` override, else "<Page title> - <Org name>".
 */
export function resolvePageSeoTitle(page: PublicPageSeo, orgName: string): string {
  const override = page.seo_title?.trim() || metaString(page.meta, "title")
  if (override) return override
  const pageTitle = page.title?.trim() || "Home"
  return `${pageTitle} - ${orgName}`
}

/**
 * The page-level meta description: `seo_description` column, else the legacy
 * `meta.description` override, else "" (callers fall back to body-derived
 * text / the org mission statement).
 */
export function resolvePageSeoDescription(page: PublicPageSeo | null): string {
  if (!page) return ""
  return page.seo_description?.trim() || metaString(page.meta, "description")
}
